/*
 * A reusable customer select component that searches customers via the API.
 * Loads initial customers on open and supports infinite scroll with pagination.
 * Search is always API-driven.
 */
import { useCallback } from 'react'
import {
  AsyncInputSelectControlField,
  CustomerLogo,
  GreyGrey70,
  Spinner,
  useModalState
} from '@sequencehq/core-components'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { getLogoSrc } from 'Customer/utils/logo'
import { CreateCustomerForm } from 'modules/Customers/components/drawers/CreateCustomer/CreateCustomerForm'
import {
  useCustomerPaginatedFetcher,
  type CustomerItem
} from 'Customer/hooks/useCustomerPaginatedFetcher'

type AsyncFieldProps = React.ComponentProps<
  typeof AsyncInputSelectControlField<CustomerItem>
>

export interface Props
  extends Pick<
    AsyncFieldProps,
    | 'label'
    | 'disabled'
    | 'isInvalid'
    | 'styles'
    | 'initiallyOpen'
    | 'validationErrors'
    | 'description'
    | 'showValidationMessage'
  > {
  onChange: (customerId: string) => void
  value?: string
  allowCreate?: boolean
  placeholder?: string
  isLoading?: boolean
  'data-testid'?: string
}

export const CustomerSelect = ({
  onChange,
  value = '',
  allowCreate = true,
  isLoading = false,
  'data-testid': dataTestId = 'customerSelect',
  disabled,
  ...selectControlProps
}: Props) => {
  const { openModal, isModalOpen, closeModal } = useModalState()
  const { paginatedFetcher, mapToOption } = useCustomerPaginatedFetcher()

  const resolveValue = useCallback(async (id: string) => {
    const response = await dashboard20240730Client.getCustomer({ id })
    const customer = response.data
    if (!customer) throw new Error('Customer not found')
    return {
      label: customer.legalName,
      value: customer.id,
      icon: <CustomerLogo src={getLogoSrc(customer)} />
    }
  }, [])

  const createDependentProps = allowCreate
    ? {
        placeholder: 'Search for a customer',
        createLabel: 'Add a customer',
        onCreate: openModal
      }
    : {
        placeholder: 'Search for a customer'
      }

  return (
    <>
      <AsyncInputSelectControlField
        {...createDependentProps}
        {...selectControlProps}
        paginatedFetcher={paginatedFetcher}
        mapToOption={mapToOption}
        onChange={onChange}
        value={value}
        disabled={disabled || isLoading}
        leadingIcon={<CustomerLogo />}
        trailingIcon={
          isLoading ? (
            <Spinner height="14px" width="14px" color={GreyGrey70} />
          ) : undefined
        }
        resolveValue={resolveValue}
        data-testid={dataTestId}
      />
      {isModalOpen && (
        <CreateCustomerForm
          onClose={closeModal}
          onSuccess={({ id }: { id: string }) => {
            closeModal()
            onChange(id)
          }}
        />
      )}
    </>
  )
}
